import React, { useState, useEffect } from 'react';

const ManageABTests = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const token = localStorage.getItem('adminToken');

  const emptyTest = {
    name: '',
    page: 'dental-implants',
    element: 'cta',
    variants: [
      { id: 'control', name: 'Control', content: { text: 'Schedule Your FREE Consultation', color: 'teal' }, weight: 50 },
      { id: 'variant-a', name: 'Variant A', content: { text: 'Get Started Today - FREE Consult', color: 'green' }, weight: 50 }
    ],
    active: true
  };

  const [formData, setFormData] = useState(emptyTest);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ab/tests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTests(data.tests || []);
    } catch (err) {
      console.error('Failed to fetch tests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/ab/tests/${editing}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/ab/tests`;
      
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        fetchTests();
        setShowForm(false);
        setEditing(null);
        setFormData(emptyTest);
      }
    } catch (err) {
      console.error('Failed to save test:', err);
    }
  };

  const handleEdit = (test) => {
    setFormData(test);
    setEditing(test.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this A/B test?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ab/tests/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTests();
    } catch (err) {
      console.error('Failed to delete test:', err);
    }
  };

  const toggleActive = async (testId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ab/tests/${testId}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTests();
    } catch (err) {
      console.error('Failed to toggle test:', err);
    }
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    if (field.startsWith('content.')) {
      const contentField = field.split('.')[1];
      newVariants[index] = {
        ...newVariants[index],
        content: { ...newVariants[index].content, [contentField]: value }
      };
    } else {
      newVariants[index] = { ...newVariants[index], [field]: value };
    }
    setFormData({ ...formData, variants: newVariants });
  };

  const addVariant = () => {
    const newVariant = {
      id: `variant-${Date.now()}`,
      name: `Variant ${String.fromCharCode(65 + formData.variants.length - 1)}`,
      content: { text: '', color: 'teal' },
      weight: 50
    };
    setFormData({ ...formData, variants: [...formData.variants, newVariant] });
  };

  const removeVariant = (index) => {
    if (formData.variants.length <= 2) return;
    const newVariants = formData.variants.filter((_, i) => i !== index);
    setFormData({ ...formData, variants: newVariants });
  };

  const pageOptions = [
    { value: 'homepage', label: 'Homepage' },
    { value: 'dental-implants', label: 'Dental Implants' },
    { value: 'invisalign', label: 'Invisalign' },
    { value: 'lanap', label: 'LANAP' },
    { value: 'specials', label: 'Specials' }
  ];

  const elementOptions = [
    { value: 'cta', label: 'CTA Button' },
    { value: 'hero', label: 'Hero Section' },
    { value: 'pricing', label: 'Pricing Section' },
    { value: 'form', label: 'Form' }
  ];

  const colorOptions = ['teal', 'green', 'blue', 'orange', 'red', 'purple'];

  if (loading) return <div className="text-center py-8">Loading A/B tests...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">A/B Testing</h2>
          <p className="text-gray-600 text-sm mt-1">Create and manage experiments to optimize conversions</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setFormData(emptyTest); }}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
        >
          + Create New Test
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editing ? 'Edit A/B Test' : 'Create New A/B Test'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Dental Implants CTA Test"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Page</label>
                  <select
                    value={formData.page}
                    onChange={(e) => setFormData({...formData, page: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    {pageOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Element</label>
                  <select
                    value={formData.element}
                    onChange={(e) => setFormData({...formData, element: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    {elementOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Variants */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">Variants</label>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="text-sm text-teal-600 hover:underline"
                  >
                    + Add Variant
                  </button>
                </div>
                
                <div className="space-y-3">
                  {formData.variants.map((variant, index) => (
                    <div key={variant.id || index} className="p-4 border rounded-lg bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <input
                          type="text"
                          value={variant.name}
                          onChange={(e) => updateVariant(index, 'name', e.target.value)}
                          className="font-medium bg-transparent border-b border-gray-300 focus:border-teal-500 outline-none"
                          placeholder="Variant name"
                        />
                        {index > 1 && (
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-red-500 hover:underline text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Button Text</label>
                          <input
                            type="text"
                            value={variant.content?.text || ''}
                            onChange={(e) => updateVariant(index, 'content.text', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded"
                            placeholder="CTA text"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Color</label>
                          <select
                            value={variant.content?.color || 'teal'}
                            onChange={(e) => updateVariant(index, 'content.color', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded"
                          >
                            {colorOptions.map(color => (
                              <option key={color} value={color}>{color}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="mt-2">
                        <label className="block text-xs text-gray-500 mb-1">Traffic Weight (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={variant.weight}
                          onChange={(e) => updateVariant(index, 'weight', parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 text-sm border rounded"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="mr-2 h-4 w-4"
                />
                <label className="text-sm font-medium text-gray-700">Start test immediately</label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditing(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  {editing ? 'Update Test' : 'Create Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tests List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {tests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">🧪</div>
            No A/B tests yet. Create your first experiment!
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4">Test Name</th>
                <th className="text-left py-3 px-4">Page</th>
                <th className="text-left py-3 px-4">Element</th>
                <th className="text-center py-3 px-4">Variants</th>
                <th className="text-center py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tests.map((test) => (
                <tr key={test.id} className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">{test.name}</td>
                  <td className="py-3 px-4 text-gray-600">{test.page}</td>
                  <td className="py-3 px-4 text-gray-600">{test.element}</td>
                  <td className="py-3 px-4 text-center">{test.variants?.length || 0}</td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => toggleActive(test.id)}
                      className={`px-2 py-1 rounded text-xs font-medium ${test.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {test.active ? 'Running' : 'Paused'}
                    </button>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => handleEdit(test)}
                      className="text-blue-600 hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(test.id)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Help Card */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <h4 className="font-medium text-purple-800">How A/B Testing Works</h4>
        <ul className="text-sm text-purple-700 mt-2 space-y-1">
          <li>• Visitors are randomly assigned to a variant based on weights</li>
          <li>• The system tracks views, clicks, and conversions for each variant</li>
          <li>• View detailed analytics in the Analytics section</li>
          <li>• Run tests for at least 2 weeks to get statistically significant results</li>
        </ul>
      </div>
    </div>
  );
};

export default ManageABTests;
